import { useEffect } from 'react';

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  schema?: object | object[];
  robots?: string;
}

function setMeta(attrName: string, attrValue: string, content: string) {
  let el = document.querySelector(`meta[${attrName}="${attrValue}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function injectJsonLd(schemas: object[]) {
  document.querySelectorAll('script[data-dynamic-jsonld]').forEach((el) => el.remove());
  schemas.forEach((s) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic-jsonld', 'true');
    script.textContent = JSON.stringify(s);
    document.head.appendChild(script);
  });
}

export function useSEO(config: SEOConfig) {
  const { title, description, keywords, canonical, schema, robots } = config;
  const schemaJson = schema ? JSON.stringify(Array.isArray(schema) ? schema : [schema]) : '';

  useEffect(() => {
    document.title = title;
    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    if (canonical) setCanonical(canonical);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    if (canonical) setMeta('property', 'og:url', canonical);

    // Handle robots meta tag
    const robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (robots) {
      if (robotsTag) {
        robotsTag.content = robots;
      } else {
        const el = document.createElement('meta');
        el.setAttribute('name', 'robots');
        el.setAttribute('content', robots);
        document.head.appendChild(el);
      }
    } else {
      if (robotsTag) robotsTag.remove();
    }

    if (schemaJson) {
      const parsed = JSON.parse(schemaJson) as object[];
      injectJsonLd(parsed);
    }

    return () => {
      document.querySelectorAll('script[data-dynamic-jsonld]').forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, canonical, schemaJson, robots]);
}
